import type { Lesson } from '@yuan-math/curriculum';
import { figure, insight, lesson, list, math, practice, steps, table, text, tip, warning } from './authoring.ts';

/**
 * Probability, rebuilt on its own foundations.
 *
 * Grade 7 defines a probability as favourable ÷ total. That definition is
 * correct exactly when the outcomes are equally likely, and every example a
 * school course reaches for — a die, a coin, a bag of counters — satisfies the
 * condition, so it never has to be stated. These six lessons state it, and each
 * one is built around the first example where the school rule quietly fails:
 *
 * - `sample-spaces` opens on the total of two dice, where seven is six times as
 *   likely as two and counting the *totals* gives 1/11.
 * - `probability-events` opens on two events that overlap, where adding the
 *   probabilities counts the overlap twice.
 * - `counting-probability` opens on a five-card hand, where the outcomes really
 *   are equally likely and the only obstacle is that there are 2,598,960 of
 *   them.
 * - `conditional-probability` opens on a table where the denominator changes.
 * - `independence` opens on drawing without replacement, where the
 *   multiplication rule students were taught first does not apply.
 * - `bayes-theorem` opens on a screening test, where the two conditional
 *   probabilities differ by a factor of fifty and the language does not
 *   distinguish them.
 */
export const PROBABILITY_UNIVERSITY_LESSONS: Lesson[] = [
  lesson({
    id: 'sample-spaces-01',
    unitId: 'unit-probability-rules',
    conceptId: 'sample-spaces',
    title: 'Sample Spaces',
    summary: 'Writing down everything that could happen — and discovering that the list you write decides the answer you get.',
    objectives: [
      'List the sample space of an experiment, including a two-stage one',
      'Say whether the outcomes of a listed sample space are equally likely',
      'Assign probabilities that satisfy the three axioms',
    ],
    estimatedMinutes: 35,
    sections: {
      introduction: {
        title: 'Two dice, and a wrong answer',
        blocks: [
          text('Roll two dice and add them. The total is somewhere between 2 and 12 — eleven possible answers. So the probability of a total of 7 is 1/11.'),
          text('That is wrong, and it is wrong in the most instructive way available. The arithmetic is fine. The counting is fine. What failed is the list.'),
          insight('Probability is not "favourable ÷ total". It is favourable ÷ total **when the outcomes on the list are equally likely** — and the eleven totals are not. There is one way to make 2 and six ways to make 7.'),
          text('The list you choose is called the **sample space**, written S, and choosing it badly is the single most common way to get a probability question wrong before any calculation begins. Everything in this course rests on choosing it well.'),
        ],
      },
      intuition: {
        title: 'A list, and three rules',
        blocks: [
          text('A **sample space** is the set of every outcome an experiment can produce, listed so that exactly one of them happens each time you run it. Two conditions are doing work in that sentence:'),
          list([
            '**Exhaustive** — every possible result is on the list. Nothing can happen that is not there.',
            '**Mutually exclusive** — no two entries can both happen. "Rolls an even number" and "rolls a 4" cannot both be entries.',
          ]),
          text('Once S is fixed, a probability is any assignment of numbers to outcomes obeying three rules — the **axioms**. Everything else in probability is a consequence of these:'),
          math('0 \\le P(A) \\le 1, \\qquad P(S) = 1, \\qquad P(A \\text{ or } B) = P(A) + P(B) \\text{ when } A, B \\text{ cannot both occur}'),
          list([
            'No probability is negative, and none exceeds 1.',
            'Something on the list happens, every time. The list totals exactly 1.',
            'Probabilities of outcomes that cannot coincide simply add.',
          ]),
          insight('The counting rule you already know is what these three axioms give you *if* you also decide every outcome deserves the same number. With n equally likely outcomes each must get 1/n, so an event containing k of them has probability k/n. The equal-likelihood assumption is a choice about the world, not a theorem.'),
          text('For two dice the honest sample space has 36 entries — every ordered pair (first die, second die) — and those 36 really are equally likely, because the dice are fair and one does not affect the other. Seven appears in six of them, so P(7) = 6/36 = 1/6, not 1/11.'),
          warning('When you can, list *ordered* outcomes. (1, 2) and (2, 1) feel like the same thing when the dice are the same colour, and merging them destroys the equal likelihood that made the list usable. Paint one die red if it helps.', 'Keep the order'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          text('All 36 ordered outcomes of two dice, with their totals. Count the sevens.'),
          table(
            ['+', '1', '2', '3', '4', '5', '6'],
            [
              ['**1**', '2', '3', '4', '5', '6', '7'],
              ['**2**', '3', '4', '5', '6', '7', '8'],
              ['**3**', '4', '5', '6', '7', '8', '9'],
              ['**4**', '5', '6', '7', '8', '9', '10'],
              ['**5**', '6', '7', '8', '9', '10', '11'],
              ['**6**', '7', '8', '9', '10', '11', '12'],
            ],
          ),
          text('The sevens run down the anti-diagonal: six of the thirty-six cells. The twelves occupy one cell. The eleven totals are a perfectly good *description* of the result and a hopeless sample space, because the cells behind them are not equally stocked.'),
          figure(
            {
              kind: 'bar-chart',
              bars: [
                { label: '2', value: 1 },
                { label: '3', value: 2 },
                { label: '4', value: 3 },
                { label: '5', value: 4 },
                { label: '6', value: 5 },
                { label: '7', value: 6 },
                { label: '8', value: 5 },
                { label: '9', value: 4 },
                { label: '10', value: 3 },
                { label: '11', value: 2 },
                { label: '12', value: 1 },
              ],
              unitLabel: 'cells of the table producing each total',
            },
            'The same 36 cells, sorted by total. Eleven outcomes, wildly unequal — which is exactly what "1/11" assumed away.',
          ),
          tip('Before assigning any probability, ask one question of your list: could I bet the same amount on every entry and feel fairly treated? If not, the list is a summary, not a sample space.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A coin is tossed three times. Write the sample space, and find the probability of exactly two heads.'),
          steps([
            { label: 'List ordered outcomes — one letter per toss, in order.', latex: 'S = \\{HHH, HHT, HTH, HTT, THH, THT, TTH, TTT\\}', note: 'Eight entries: two choices, three times.' },
            { label: 'Check the list is exhaustive and exclusive.', latex: '|S| = 2^{3} = 8', note: 'Every sequence of three tosses is there exactly once.' },
            { label: 'Are they equally likely?', latex: 'P(\\text{each}) = \\tfrac{1}{8}', note: 'A fair coin, and the tosses do not influence each other.' },
            { label: 'Count the outcomes with exactly two heads.', latex: 'HHT, HTH, THH \\Rightarrow 3' },
            { label: 'Divide.', latex: 'P(\\text{exactly two heads}) = \\tfrac{3}{8}' },
          ]),
          text('Compare the tempting shortcut: "the number of heads is 0, 1, 2 or 3, so P(2 heads) = 1/4". Same mistake as the dice, in a smaller costume — those four counts are not equally likely, and the honest list of eight says so.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('sample-spaces', 4)] },
      independent: { title: 'On your own', blocks: [practice('sample-spaces', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('sample-spaces', 3, 'Sample spaces where the obvious list is the wrong one.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'A sample space lists every outcome, with exactly one occurring per run.',
            'Favourable ÷ total is only valid when the listed outcomes are equally likely.',
            'Summarising outcomes — totals, counts, categories — usually destroys equal likelihood.',
            'The three axioms: probabilities lie in [0, 1], the whole space has probability 1, and exclusive events add.',
            'For a multi-stage experiment, list the stages in order and keep them ordered.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'probability-events-01',
    unitId: 'unit-probability-rules',
    conceptId: 'probability-events',
    title: 'Events and the Addition Rule',
    summary: 'Building events with "and", "or" and "not" — and the overlap that gets counted twice if you are not watching.',
    objectives: [
      'Use the complement rule to answer an "at least one" question',
      'Apply the addition rule, including the subtraction of the intersection',
      'Distinguish mutually exclusive events from independent ones',
    ],
    estimatedMinutes: 40,
    sections: {
      introduction: {
        title: 'A card that is both',
        blocks: [
          text('Draw one card from a standard deck. What is the probability it is a heart or a face card?'),
          text('There are 13 hearts and 12 face cards, so 13/52 + 12/52 = 25/52. Except that the king, queen and jack of hearts have now been counted twice — once as hearts, once as face cards.'),
          math('P(\\text{heart or face}) = \\frac{13}{52} + \\frac{12}{52} - \\frac{3}{52} = \\frac{22}{52} = \\frac{11}{26}'),
          insight('An **event** is a subset of the sample space, and subsets can overlap. The third axiom licenses adding probabilities only for events that *cannot* both occur; the moment they can, the shared part is on both lists and has to come off once.'),
        ],
      },
      intuition: {
        title: 'Three operations and three rules',
        blocks: [
          text('Events are sets, so the three ways of building a new one from old ones are the three set operations, and each carries a rule:'),
          table(
            ['In words', 'In symbols', 'The rule'],
            [
              ['A and B', 'A ∩ B', 'no general shortcut — needs conditional probability'],
              ['A or B (or both)', 'A ∪ B', 'P(A) + P(B) − P(A ∩ B)'],
              ['not A', "A′ or Aᶜ", '1 − P(A)'],
            ],
          ),
          text('The **complement rule** looks like the least of the three and does the most work. Any question containing "at least one" is a complement question in disguise, because "at least one" has many ways to happen and its opposite — "none" — usually has one.'),
          math("P(\\text{at least one}) = 1 - P(\\text{none})"),
          insight('Six dice, and you want the probability of at least one six. Directly, that is exactly-one plus exactly-two plus … plus exactly-six: six calculations. By complement it is 1 − (5/6)⁶, which is one.'),
          text('The **addition rule** is the one with the correction term:'),
          math('P(A \\cup B) = P(A) + P(B) - P(A \\cap B)'),
          text('When A and B cannot both happen — **mutually exclusive** events — the intersection is empty, the correction is zero, and the rule collapses back to the axiom.'),
          warning('Mutually exclusive is not independent. They are near-opposites. If A and B are mutually exclusive and A happens, then B definitely did *not*: knowing about A is maximally informative about B. Independent events are the ones where knowing tells you nothing. The only events that are both are the ones with probability zero.', 'Exclusive ≠ independent'),
          text('A last piece of vocabulary worth having early: a **partition** is a set of events that are mutually exclusive and exhaustive between them — exactly one occurs. Their probabilities total 1, which is the fact every "and the rest?" question is answered with.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'venn',
              labels: { a: 'Heart', b: 'Face card' },
              regions: { onlyA: '10', both: '3', onlyB: '9', neither: '30 others' },
              universeLabel: '52 cards',
              label: 'the overlap belongs to both circles',
            },
            'Ten hearts that are not face cards, nine face cards that are not hearts, three cards that are both. Adding 13 and 12 counts that middle group twice — the addition rule takes it off once.',
          ),
          figure(
            {
              kind: 'venn',
              labels: { a: 'Heart', b: 'Face card' },
              regions: { onlyA: '10', both: '3', onlyB: '9', neither: '30 others' },
              highlight: ['onlyA', 'both', 'onlyB'],
              universeLabel: '52 cards',
              label: 'A ∪ B',
            },
            'The union shaded: 10 + 3 + 9 = 22 cards. Every region is counted once, which is what the rule is arranging.',
          ),
          figure(
            {
              kind: 'venn',
              labels: { a: 'Heart', b: 'Face card' },
              regions: { onlyA: '10', both: '3', onlyB: '9', neither: '30 others' },
              highlight: ['neither'],
              universeLabel: '52 cards',
              label: '(A ∪ B)′',
            },
            'The complement of the union: 30 cards that are neither. 22 + 30 = 52, which is the check worth doing on every answer of this kind.',
          ),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('In a class of 40, 22 study French, 17 study German, and 6 study both. Pick a student at random. Find the probability that they study at least one of the two languages, and the probability that they study neither.'),
          steps([
            { label: 'Name the events and their probabilities.', latex: 'P(F) = \\tfrac{22}{40}, \\quad P(G) = \\tfrac{17}{40}, \\quad P(F \\cap G) = \\tfrac{6}{40}' },
            { label: 'Apply the addition rule.', latex: 'P(F \\cup G) = \\tfrac{22}{40} + \\tfrac{17}{40} - \\tfrac{6}{40} = \\tfrac{33}{40}', note: 'The 6 are in both counts; take them off once.' },
            { label: 'The rest study neither — the complement.', latex: "P((F \\cup G)') = 1 - \\tfrac{33}{40} = \\tfrac{7}{40}" },
            { label: 'Sanity check by counting people.', latex: '16 + 6 + 11 + 7 = 40', note: 'French only, both, German only, neither.' },
          ]),
          text('Notice the answer without the correction: 22/40 + 17/40 = 39/40, leaving one student studying neither. Six people were double-counted, and the error is exactly their size.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('probability-events', 4)] },
      independent: { title: 'On your own', blocks: [practice('probability-events', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('probability-events', 3, 'Complements, unions, and events that are not what they look like.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'An event is a subset of the sample space; "and", "or" and "not" are intersection, union and complement.',
            'P(A ∪ B) = P(A) + P(B) − P(A ∩ B), and the last term is zero only for mutually exclusive events.',
            "P(A′) = 1 − P(A), which is how every \"at least one\" question should be attacked.",
            'Mutually exclusive means they cannot coincide; independent means one tells you nothing about the other. They are not the same idea.',
            'A partition splits the sample space into exclusive, exhaustive pieces whose probabilities total 1.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'counting-probability-01',
    unitId: 'unit-probability-rules',
    conceptId: 'counting-probability',
    title: 'Counting to Find Probability',
    summary: 'When the sample space has two and a half million entries, you count it rather than write it — and one condition makes that legal.',
    objectives: [
      'Decide whether an arrangement question needs a permutation or a combination',
      'Find a probability as a ratio of two counts',
      'Say why the counting method needs equally likely outcomes',
    ],
    estimatedMinutes: 40,
    sections: {
      introduction: {
        title: 'Two and a half million hands',
        blocks: [
          text('Deal five cards from a shuffled deck. What is the probability that all five are hearts?'),
          text('The sample space is every five-card hand, and there are 2,598,960 of them. Nobody is listing that. But every one of them is equally likely — the shuffle sees to that — so the probability is still a ratio of two counts, and counting is a thing that can be done without listing.'),
          math('P(\\text{all hearts}) = \\frac{{}^{13}C_{5}}{{}^{52}C_{5}} = \\frac{1287}{2598960} \\approx 0.000495'),
          insight('This is the whole technique of the lesson, and its precondition is the same one as always: the outcomes being counted have to be equally likely. Counting *hands* works because hands are equally likely. Counting *suit patterns* would not be.'),
        ],
      },
      intuition: {
        title: 'Which count, and why',
        blocks: [
          text('The counting tools are the ones from combinatorics. What is new is deciding which one the *numerator* and *denominator* each need — and they must agree.'),
          table(
            ['Question', 'Tool', 'Formula'],
            [
              ['How many ways to fill k slots from n, order mattering?', 'permutation', 'ⁿPₖ = n!/(n − k)!'],
              ['How many ways to choose k from n, order not mattering?', 'combination', 'ⁿCₖ = n!/(k!(n − k)!)'],
              ['How many outcomes over several independent stages?', 'multiplication', 'n₁ × n₂ × ⋯'],
            ],
          ),
          warning('Numerator and denominator must count the same *kind* of thing. Count the hands as unordered in the denominator and as ordered in the numerator and you get an answer out by a factor of 120. If in doubt, order both — the factor cancels and the answer is unchanged.', 'Count both ends the same way'),
          text('The reliable procedure for a probability by counting is four steps:'),
          list(
            [
              'Decide what one outcome *is* — a hand, a sequence, a committee — and count how many there are. That is the denominator.',
              'Check that those outcomes are equally likely. Random dealing, shuffling and drawing all deliver this; "picking a total" and "picking a shape" do not.',
              'Count the outcomes with the property you want, using the same convention. That is the numerator.',
              'Divide, and check the answer lies between 0 and 1.',
            ],
            true,
          ),
          insight('A favourite trap of this topic: "at least one". The count of hands with at least one ace is a four-term sum; the count with *no* ace is a single combination. Count the complement and subtract, exactly as in the previous lesson.'),
          text('Another pattern worth recognising, because it appears in quality control, in card games and in survey sampling: choosing a group from two pools at once. To pick 3 women and 2 men from 7 and 5, choose within each pool and multiply — ⁷C₃ × ⁵C₂ — because every choice of women can accompany every choice of men.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          text('Why the same problem gets two different counts, and why the ratio does not care — five cards, all hearts:'),
          table(
            ['Convention', 'Numerator', 'Denominator', 'Ratio'],
            [
              ['Unordered hands', '¹³C₅ = 1287', '⁵²C₅ = 2,598,960', '0.000495'],
              ['Ordered deals', '¹³P₅ = 154,440', '⁵²P₅ = 311,875,200', '0.000495'],
              ['Mixed (wrong)', '¹³P₅ = 154,440', '⁵²C₅ = 2,598,960', '0.0594 — impossible'],
            ],
          ),
          text('The first two agree exactly, because the factor of 5! multiplies both counts. The third is a hundred and twenty times too big, and it is the answer a student gets by reaching for the formula they remember rather than the one that matches the denominator they wrote.'),
          figure(
            {
              kind: 'venn',
              labels: { a: 'Hands with an ace', b: 'Hands with a king' },
              regions: { onlyA: '?', both: '?', onlyB: '?', neither: "⁴⁸C₅" },
              universeLabel: 'all ⁵²C₅ hands',
              label: 'count the outside first',
            },
            'The complement is one combination; the three inner regions are three. "At least one ace" is a subtraction, not a sum.',
          ),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A committee of 4 is chosen at random from 7 women and 5 men. Find the probability that it contains exactly 2 women, and the probability that it contains at least 1 man.'),
          steps([
            { label: 'Count all committees — unordered, since a committee has no ranking.', latex: '{}^{12}C_{4} = 495' },
            { label: 'Count committees with exactly 2 women: choose 2 from 7, and 2 from 5.', latex: '{}^{7}C_{2} \\times {}^{5}C_{2} = 21 \\times 10 = 210' },
            { label: 'Divide.', latex: 'P(\\text{exactly 2 women}) = \\tfrac{210}{495} = \\tfrac{14}{33} \\approx 0.424' },
            { label: 'For "at least 1 man", count the complement: an all-woman committee.', latex: '{}^{7}C_{4} = 35' },
            { label: 'Subtract from 1.', latex: 'P(\\text{at least 1 man}) = 1 - \\tfrac{35}{495} = \\tfrac{460}{495} = \\tfrac{92}{99} \\approx 0.929' },
          ]),
          text('The multiplication in step 2 is the step to be able to justify rather than recite: each of the 21 pairs of women can be joined by any of the 10 pairs of men, so the choices multiply. Adding them — 21 + 10 = 31 — answers a question nobody asked.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('counting-probability', 4)] },
      independent: { title: 'On your own', blocks: [practice('counting-probability', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('counting-probability', 3, 'Two pools, complements, and choosing between ⁿPₖ and ⁿCₖ.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'A probability is a ratio of counts only when the counted outcomes are equally likely.',
            'Order matters ⇒ permutation; order does not ⇒ combination. Choose once and use the same convention top and bottom.',
            'Choices at independent stages multiply; alternatives at one stage add.',
            'Choosing from two pools at once means multiplying a combination from each.',
            '"At least one" is almost always 1 − P(none).',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'conditional-probability-01',
    unitId: 'unit-conditional-probability',
    conceptId: 'conditional-probability',
    title: 'Conditional Probability',
    summary: 'What a probability becomes once you know something — and which number goes underneath when it does.',
    objectives: [
      'Compute P(A | B) from a two-way table and from the definition',
      'Use the multiplication rule for two-stage experiments',
      'Distinguish P(A | B) from P(B | A) and from P(A ∩ B)',
    ],
    estimatedMinutes: 45,
    sections: {
      introduction: {
        title: 'The denominator moves',
        blocks: [
          text('A card is drawn from a deck. P(king) = 4/52 = 1/13. Now someone glances at it and tells you it is a face card. What is the probability it is a king?'),
          text('Not 1/13. There are 12 face cards, 4 of them kings, so the answer is 4/12 = 1/3. Nothing about the card changed; what changed is the set of things it could be.'),
          insight('A conditional probability is a probability computed in a **shrunken sample space**. Being told B happened does not alter the world — it removes from consideration every outcome where B did not happen, and the surviving outcomes have to be re-scaled so they total 1 again.'),
          math('P(A \\mid B) = \\frac{P(A \\cap B)}{P(B)}, \\qquad P(B) > 0'),
          text('The numerator is the part of A that survives; the denominator is everything that survives. That division by P(B) is the re-scaling, and it is why the condition sits underneath.'),
        ],
      },
      intuition: {
        title: 'Reading it off a table, and running it forwards',
        blocks: [
          text('The fastest way to feel what conditioning does is a two-way table of counts. Conditioning on a row means using that row’s total as the denominator instead of the grand total.'),
          table(
            ['', 'Owns a bike', 'No bike', 'Total'],
            [
              ['Under 30', '48', '32', '80'],
              ['30 or over', '27', '93', '120'],
              ['**Total**', '**75**', '**125**', '**200**'],
            ],
          ),
          list([
            'P(bike) = 75/200 = 0.375 — the grand total underneath.',
            'P(bike | under 30) = 48/80 = 0.600 — the row total underneath.',
            'P(under 30 | bike) = 48/75 = 0.640 — the column total underneath.',
            'P(bike ∩ under 30) = 48/200 = 0.240 — the grand total again, and a different question from both.',
          ]),
          warning('The last three share a numerator and differ only in what is beneath. Deciding which denominator the question asked for is the whole skill; the arithmetic never is.', 'Same top, three bottoms'),
          text('Rearranging the definition gives the **multiplication rule**, which is how two-stage experiments are actually computed:'),
          math('P(A \\cap B) = P(B)\\,P(A \\mid B)'),
          text('Read left to right along a tree: the probability of a path is the probability of the first branch times the probability of the second *given the first*. For drawing without replacement, the second factor is where the changed contents of the bag are recorded.'),
          insight('The **law of total probability** falls out of the same picture. If B₁ … Bₖ partition the sample space, then A is reached along k different paths and P(A) = ΣP(Bᵢ)P(A | Bᵢ). That sum is the denominator of Bayes’ theorem two lessons from now.'),
          warning('P(A | B) and P(B | A) are different numbers and English barely distinguishes them. "The probability of a bike given they are under 30" is 0.600; "the probability of being under 30 given a bike" is 0.640. Write the vertical bar down and put the *known* thing on the right of it before computing anything.', 'The bar is not symmetric'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'venn',
              labels: { a: 'Under 30', b: 'Owns a bike' },
              regions: { onlyA: '32', both: '48', onlyB: '27', neither: '93' },
              highlight: ['onlyA', 'both'],
              universeLabel: '200 people',
              label: 'conditioning on "under 30"',
            },
            'Conditioning on the left circle throws away everything outside it. Of the 80 that remain, 48 own a bike: P(bike | under 30) = 48/80.',
          ),
          figure(
            {
              kind: 'venn',
              labels: { a: 'Under 30', b: 'Owns a bike' },
              regions: { onlyA: '32', both: '48', onlyB: '27', neither: '93' },
              highlight: ['both', 'onlyB'],
              universeLabel: '200 people',
              label: 'conditioning on "owns a bike"',
            },
            'The same diagram, conditioned the other way. The shaded set has 75 members, 48 of them under 30: P(under 30 | bike) = 48/75. Same overlap, different denominator, different answer.',
          ),
          figure(
            {
              kind: 'probability-tree',
              branches: [
                {
                  label: 'Red',
                  probability: '5/9',
                  children: [
                    { label: 'Red', probability: '4/8', outcome: '20/72' },
                    { label: 'Blue', probability: '4/8', outcome: '20/72' },
                  ],
                },
                {
                  label: 'Blue',
                  probability: '4/9',
                  children: [
                    { label: 'Red', probability: '5/8', outcome: '20/72' },
                    { label: 'Blue', probability: '3/8', outcome: '12/72' },
                  ],
                },
              ],
              label: 'two draws, no replacement',
            },
            'Five red and four blue counters, drawn without replacement. Every second-stage probability has 8 underneath, because one counter has gone — that is the conditioning, drawn.',
          ),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A bag holds 5 red and 4 blue counters. Two are drawn without replacement. Find the probability that both are red, and the probability that the second is blue given the first was red.'),
          steps([
            { label: 'First draw: five reds out of nine counters.', latex: 'P(R_1) = \\tfrac{5}{9}' },
            { label: 'Second draw given the first was red: four reds remain out of eight.', latex: 'P(R_2 \\mid R_1) = \\tfrac{4}{8} = \\tfrac{1}{2}' },
            { label: 'Multiply along the path.', latex: 'P(R_1 \\cap R_2) = \\tfrac{5}{9} \\times \\tfrac{1}{2} = \\tfrac{5}{18}' },
            { label: 'For the second question, read the branch directly.', latex: 'P(B_2 \\mid R_1) = \\tfrac{4}{8} = \\tfrac{1}{2}', note: 'All four blues remain; one red has gone, leaving eight.' },
          ]),
          text('The two answers are different kinds of thing, and it is worth naming the difference. 5/18 is a probability about the whole experiment, computed before anything is drawn. 1/2 is a probability about the second draw only, computed in a world where the first draw has already come up red.'),
          warning('If the counter had been replaced, every second-stage denominator would be 9 again and P(R₂ | R₁) would equal P(R₂). That case has a name, and it is the next lesson.', 'Replacement changes everything'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('conditional-probability', 4)] },
      independent: { title: 'On your own', blocks: [practice('conditional-probability', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('conditional-probability', 3, 'Tables read both ways, and trees drawn without replacement.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'P(A | B) = P(A ∩ B) ÷ P(B): the condition goes underneath.',
            'Conditioning shrinks the sample space to B and re-scales what is left.',
            'On a two-way table, a conditional probability uses a row or column total, never the grand total.',
            'P(A ∩ B) = P(B)P(A | B) — multiply along a branch of the tree.',
            'The law of total probability adds the paths: P(A) = ΣP(Bᵢ)P(A | Bᵢ).',
            'P(A | B) ≠ P(B | A). Decide which is known before you divide.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'independence-01',
    unitId: 'unit-conditional-probability',
    conceptId: 'independence',
    title: 'Independence',
    summary: 'The case where knowing tells you nothing — and the multiplication rule you may only use once you have checked.',
    objectives: [
      'Test two events for independence from probabilities or a table',
      'Use P(A ∩ B) = P(A)P(B) only where it is justified',
      'Explain why sampling without replacement makes trials dependent',
    ],
    estimatedMinutes: 40,
    sections: {
      introduction: {
        title: 'A rule taught backwards',
        blocks: [
          text('Most people meet independence as a formula: P(A and B) = P(A) × P(B). Learned that way, it becomes the thing you do to two probabilities whenever a question says "and" — and students then multiply their way through card problems, quality-control problems and medical problems where the rule does not hold.'),
          text('The definition is not the formula. The definition is a statement about information:'),
          math('A \\text{ and } B \\text{ are independent when } P(A \\mid B) = P(A)'),
          insight('Knowing that B happened leaves the probability of A exactly where it was. The multiplication rule is then a *consequence*: substitute P(A | B) = P(A) into P(A ∩ B) = P(B)P(A | B) and out drops P(A)P(B).'),
          text('Stated in that order, the precondition is impossible to lose, because it is the definition rather than a footnote to the formula.'),
        ],
      },
      intuition: {
        title: 'Three equivalent tests, and where it fails',
        blocks: [
          text('Any one of these three establishes independence, and each is the easiest one in some situation:'),
          list(
            [
              'P(A | B) = P(A) — the conditional equals the unconditional.',
              'P(B | A) = P(B) — independence is symmetric, despite conditioning not being.',
              'P(A ∩ B) = P(A)P(B) — the multiplication test, the one to use when you have a table of counts.',
            ],
            true,
          ),
          text('The physical situations that produce independence are worth listing, because outside them you should assume dependence until shown otherwise:'),
          table(
            ['Situation', 'Independent?', 'Why'],
            [
              ['Successive coin tosses, dice rolls', 'yes', 'nothing carries over between trials'],
              ['Drawing *with* replacement', 'yes', 'the bag is restored before the next draw'],
              ['Drawing *without* replacement', 'no', 'the contents changed'],
              ['Two components in the same machine', 'usually not', 'shared heat, shared power supply, shared operator'],
              ['Two events on the same two-way table', 'test it', 'only the arithmetic can say'],
            ],
          ),
          warning('A large population makes dependence *small*, not absent. Drawing 2 people from 10,000 without replacement changes the second probability in the fifth decimal place, and treating them as independent is a deliberate approximation. Drawing 2 counters from 9 changes it enormously.', 'Small dependence is still dependence'),
          insight('Independence is a property of the *probabilities*, not of the story. On one roll of a fair die, "even" and "at most 4" are independent — P(even) = 1/2, and P(even | at most 4) = 2/4 = 1/2 — even though both events are about the same single roll. Change the second event to "at most 3" and independence vanishes: P(even | at most 3) = 1/3. No verbal argument distinguishes those two cases; only the arithmetic does.'),
          text('And once you have several independent events, they multiply all the way along, which is what makes "at least one" tractable:'),
          math('P(\\text{no success in } n \\text{ tries}) = (1 - p)^{n}, \\qquad P(\\text{at least one}) = 1 - (1 - p)^{n}'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'probability-tree',
              branches: [
                {
                  label: 'Red',
                  probability: '5/9',
                  children: [
                    { label: 'Red', probability: '5/9' },
                    { label: 'Blue', probability: '4/9' },
                  ],
                },
                {
                  label: 'Blue',
                  probability: '4/9',
                  children: [
                    { label: 'Red', probability: '5/9' },
                    { label: 'Blue', probability: '4/9' },
                  ],
                },
              ],
              label: 'replacement: the branches repeat',
            },
            'With replacement, both second-stage splits are identical to the first. That repetition *is* independence — the first result has left no trace.',
          ),
          figure(
            {
              kind: 'probability-tree',
              branches: [
                {
                  label: 'Red',
                  probability: '5/9',
                  children: [
                    { label: 'Red', probability: '4/8' },
                    { label: 'Blue', probability: '4/8' },
                  ],
                },
                {
                  label: 'Blue',
                  probability: '4/9',
                  children: [
                    { label: 'Red', probability: '5/8' },
                    { label: 'Blue', probability: '3/8' },
                  ],
                },
              ],
              label: 'no replacement: the branches differ',
            },
            'Without replacement the two second-stage splits disagree — 4/8 against 5/8. Whenever the second stage looks different depending on the first, the events are dependent and P(A)P(B) is the wrong calculation.',
          ),
          tip('The tree is the test. Draw the second stage twice; if the two sets of branch probabilities are identical, the stages are independent.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('From the survey table below, are "owns a bike" and "under 30" independent? And separately: a machine part fails with probability 0.04, independently each day. What is the probability it survives a 5-day week?'),
          table(
            ['', 'Owns a bike', 'No bike', 'Total'],
            [
              ['Under 30', '48', '32', '80'],
              ['30 or over', '27', '93', '120'],
              ['**Total**', '**75**', '**125**', '**200**'],
            ],
          ),
          steps([
            { label: 'Compute the two marginals and the joint.', latex: 'P(\\text{bike}) = \\tfrac{75}{200} = 0.375, \\quad P(\\text{under 30}) = \\tfrac{80}{200} = 0.4' },
            { label: 'What the multiplication test predicts if they were independent.', latex: '0.375 \\times 0.4 = 0.15' },
            { label: 'What the table actually shows.', latex: 'P(\\text{bike} \\cap \\text{under 30}) = \\tfrac{48}{200} = 0.24' },
            { label: 'Compare.', latex: '0.24 \\ne 0.15', note: 'Not independent — being under 30 raises the chance of a bike from 0.375 to 0.6.' },
            { label: 'Now the machine. Independent days, so multiply the survivals.', latex: 'P(\\text{survives 5 days}) = 0.96^{5} \\approx 0.815' },
            { label: 'And therefore the chance of at least one failure.', latex: '1 - 0.815 = 0.185' },
          ]),
          text('The first part is the one worth dwelling on. Nothing in the story said whether age and bike ownership were related; only the arithmetic could answer, and it did — 0.24 against a predicted 0.15 is a substantial association, and any calculation that had assumed independence would have been out by 60%.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('independence', 4)] },
      independent: { title: 'On your own', blocks: [practice('independence', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('independence', 3, 'Testing independence, and "at least one" over repeated independent trials.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Independent means P(A | B) = P(A): knowing B changes nothing about A.',
            'P(A ∩ B) = P(A)P(B) holds *because* of independence, and only then.',
            'The multiplication test is the practical one on a table of counts.',
            'Sampling with replacement gives independence; without replacement does not.',
            'Independent and mutually exclusive are opposite ideas, not similar ones.',
            'For n independent trials, P(at least one success) = 1 − (1 − p)ⁿ.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'bayes-theorem-01',
    unitId: 'unit-conditional-probability',
    conceptId: 'bayes-theorem',
    title: "Bayes' Theorem",
    summary: 'Running a conditional probability backwards — and the accurate medical test whose positives are mostly wrong.',
    objectives: [
      "Apply Bayes' theorem to reverse a conditional probability",
      'Compute the denominator with the law of total probability',
      'Explain why a rare condition makes most positive tests false',
    ],
    estimatedMinutes: 45,
    sections: {
      introduction: {
        title: 'A 99% accurate test',
        blocks: [
          text('A disease affects 1 person in 1000. A test for it catches 99% of the people who have it, and wrongly flags only 2% of the people who do not. You test positive. What is the probability you have the disease?'),
          text('Most people, including most doctors asked this question in studies, answer somewhere near 99%. The answer is about 4.7%.'),
          insight('The test statistic quoted — 99% — is P(positive | disease). What you want is P(disease | positive). These are different numbers, and when the disease is rare they are wildly different, because the 2% false-positive rate is applied to a group a thousand times larger than the 99% is.'),
          text('**Bayes’ theorem** is the arithmetic that turns one into the other. It is two lines of algebra, and it is on this course because reading a conditional probability in the wrong direction is the most consequential error in applied probability — it appears in medicine, in law, in spam filtering and in every argument that begins "but the test is 99% accurate".'),
        ],
      },
      intuition: {
        title: 'The formula, and the picture underneath it',
        blocks: [
          text('Start from the definition of conditional probability, written both ways round:'),
          math('P(A \\mid B)\\,P(B) = P(A \\cap B) = P(B \\mid A)\\,P(A)'),
          text('Divide by P(B) and you have the theorem:'),
          math('P(A \\mid B) = \\frac{P(B \\mid A)\\,P(A)}{P(B)}'),
          text('The denominator is almost never given directly, and is assembled with the law of total probability — B happens along the A path or along the not-A path:'),
          math("P(B) = P(B \\mid A)P(A) + P(B \\mid A')P(A')"),
          insight('Put together, every Bayes calculation is the same shape: **the path you care about, divided by all the paths that end where you are**. Draw the tree, multiply along each branch, and the answer is one path’s product over the total of the products that reach the observed outcome.'),
          text('The three quantities have names worth knowing, because they say what the theorem is doing:'),
          list([
            '**Prior** P(A) — what you believed before the evidence. Here, the 1-in-1000 base rate.',
            '**Likelihood** P(B | A) — how well the evidence is explained by A. Here, the 99% detection rate.',
            '**Posterior** P(A | B) — what you believe after. Here, 4.7%.',
          ]),
          warning('Ignoring the prior is the **base-rate fallacy**, and it is what produces the 99% answer. A rare condition means the true positives are drawn from a tiny group and the false positives from an enormous one, so the false positives can outnumber them even when the error rate is small.', 'The base rate is not a detail'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          text('Take 100,000 people, so that every group is a whole number of humans rather than a decimal.'),
          table(
            ['', 'Has the disease', 'Does not', 'Total'],
            [
              ['Tests positive', '99', '1998', '2097'],
              ['Tests negative', '1', '97,902', '97,903'],
              ['**Total**', '**100**', '**99,900**', '**100,000**'],
            ],
          ),
          text('Read along the "tests positive" row: 2097 people got a positive result, and only 99 of them are ill. 99 ÷ 2097 = 0.047. No formula was needed — the table *is* Bayes’ theorem, with the multiplications done in advance.'),
          figure(
            {
              kind: 'probability-tree',
              branches: [
                {
                  label: 'Disease',
                  probability: '0.001',
                  children: [
                    { label: 'Positive', probability: '0.99', outcome: '0.00099' },
                    { label: 'Negative', probability: '0.01', outcome: '0.00001' },
                  ],
                },
                {
                  label: 'No disease',
                  probability: '0.999',
                  children: [
                    { label: 'Positive', probability: '0.02', outcome: '0.01998' },
                    { label: 'Negative', probability: '0.98', outcome: '0.97902' },
                  ],
                },
              ],
              highlightPaths: [
                [0, 0],
                [1, 0],
              ],
              label: 'both roads to a positive',
            },
            'The two highlighted paths are the only ways to test positive. The lower one is twenty times heavier than the upper one, because it starts from a branch that carries 999 people in every 1000.',
          ),
          tip('Whenever a Bayes question resists, put 10,000 or 100,000 imaginary people through the tree and count them. Whole people are harder to misplace than four-decimal probabilities, and the answer is identical.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Two factories supply a shop. Factory A makes 60% of the items and 3% of its items are faulty; factory B makes 40% and 7% of its items are faulty. An item is found to be faulty. What is the probability it came from factory A?'),
          steps([
            { label: 'Write the priors and the likelihoods.', latex: 'P(A) = 0.6,\\; P(F \\mid A) = 0.03; \\quad P(B) = 0.4,\\; P(F \\mid B) = 0.07' },
            { label: 'Multiply along each path to a fault.', latex: 'P(A \\cap F) = 0.6 \\times 0.03 = 0.018; \\quad P(B \\cap F) = 0.4 \\times 0.07 = 0.028' },
            { label: 'Total probability of a fault — the denominator.', latex: 'P(F) = 0.018 + 0.028 = 0.046' },
            { label: 'The path you want, over all the paths that got here.', latex: 'P(A \\mid F) = \\frac{0.018}{0.046} \\approx 0.391' },
          ]),
          text('Notice how the answer sits between two things you might have guessed. Factory A makes most of the items, so before seeing the fault you would have said 0.6. Factory A is the more reliable factory, so the fault is evidence against it — and 0.391 is 0.6 revised downwards by exactly the weight of that evidence.'),
          warning('The denominator is not P(F | A) + P(F | B) = 0.10. Conditional probabilities with different conditions do not add to anything meaningful; what add are the *joint* probabilities of the paths, 0.018 and 0.028.', 'Add the paths, not the rates'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('bayes-theorem', 4)] },
      independent: { title: 'On your own', blocks: [practice('bayes-theorem', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('bayes-theorem', 3, 'Reversing conditionals where the base rate does most of the work.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            "Bayes' theorem: P(A | B) = P(B | A)P(A) ÷ P(B).",
            "The denominator is built from the law of total probability: P(B|A)P(A) + P(B|A′)P(A′).",
            'Every Bayes answer is one path divided by the total of the paths reaching the observed outcome.',
            'Prior, likelihood, posterior — the theorem revises a belief in the light of evidence.',
            'A rare condition plus a small false-positive rate makes most positives false. That is the base-rate fallacy.',
            'When stuck, run 100,000 imaginary people through a table and count.',
          ]),
        ],
      },
    },
  }),
];
